import type { Conditions, TimeOfDay } from '@accra/shared';

/** Everything that changes how the road looks for a weather and time of day. Pure numbers and colours. */
export interface Look {
  sunElevationDeg: number;
  sunColor: string;
  sunIntensity: number;
  hemiSky: string;
  hemiGround: string;
  hemiIntensity: number;
  environmentIntensity: number;
  exposure: number;
  fog: { color: string; near: number; far: number };
  sky: { turbidity: number; rayleigh: number; mie: number; mieG: number };
  /** 0..1: how wet the road is. */
  wet: number;
  rain: boolean;
  /** 0..1: how much lights glow (lamps, shop signs, tail lights). */
  night: number;
  headlights: boolean;
}

const DAY: Look = {
  sunElevationDeg: 42, sunColor: '#ffe9c9', sunIntensity: 2.5, hemiSky: '#e8e4da', hemiGround: '#5e4b36', hemiIntensity: 0.35,
  environmentIntensity: 0.28, exposure: 0.82, fog: { color: '#cdd3d6', near: 120, far: 380 },
  sky: { turbidity: 8, rayleigh: 1.1, mie: 0.006, mieG: 0.8 }, wet: 0, rain: false, night: 0, headlights: false,
};

const TIMES: Record<TimeOfDay, Look> = {
  day: DAY,
  dusk: {
    sunElevationDeg: 4, sunColor: '#ff9a5a', sunIntensity: 1.3, hemiSky: '#c98f78', hemiGround: '#3b2c2c', hemiIntensity: 0.28,
    environmentIntensity: 0.14, exposure: 0.9, fog: { color: '#b98770', near: 70, far: 300 },
    sky: { turbidity: 10, rayleigh: 2.4, mie: 0.02, mieG: 0.92 }, wet: 0, rain: false, night: 0.5, headlights: true,
  },
  night: {
    sunElevationDeg: -14, sunColor: '#7f93d9', sunIntensity: 0.45, hemiSky: '#34406e', hemiGround: '#0d1020', hemiIntensity: 0.3,
    environmentIntensity: 0.06, exposure: 1.05, fog: { color: '#070a14', near: 25, far: 190 },
    sky: { turbidity: 1, rayleigh: 0.08, mie: 0.001, mieG: 0.7 }, wet: 0, rain: false, night: 1, headlights: true,
  },
};

/** Mixes two #rrggbb colours (t = 0 gives a, 1 gives b). */
export function mixColour(a: string, b: string, t: number): string {
  const parse = (hex: string): number[] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  const channel = (x: number, y: number): string => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${channel(r1!, r2!)}${channel(g1!, g2!)}${channel(b1!, b2!)}`;
}

/** How bright the lighting is, 0 (night) to 1 (day): used to keep fog colours dark at night. */
function daylight(look: Look): number {
  return Math.min(1, Math.max(0, (look.sunElevationDeg + 14) / 56));
}

/** The look for a shift: the time of day first, then the weather on top. */
export function lookFor(conditions: Conditions): Look {
  const base = TIMES[conditions.time];
  const look: Look = { ...base, fog: { ...base.fog }, sky: { ...base.sky } };
  const light = daylight(base);
  if (conditions.weather === 'rain') {
    look.sunIntensity *= 0.4;
    look.sunColor = mixColour(look.sunColor, '#dbe3ec', 0.6);
    look.hemiIntensity *= 1.25;
    look.environmentIntensity *= 0.8;
    look.exposure *= 1.03;
    look.fog = { color: mixColour(base.fog.color, '#7b848b', 0.7 * light), near: base.fog.near * 0.35, far: base.fog.far * 0.55 };
    look.sky = { turbidity: 22, rayleigh: look.sky.rayleigh * 0.35, mie: 0.045, mieG: 0.6 };
    look.wet = 1;
    look.rain = true;
    look.headlights = true;
  } else if (conditions.weather === 'harmattan') {
    look.sunIntensity *= 0.7;
    look.sunColor = mixColour(look.sunColor, '#ffc77a', 0.7);
    look.hemiSky = mixColour(look.hemiSky, '#d6b27c', 0.4 * light);
    look.environmentIntensity *= 0.75;
    look.fog = { color: mixColour(base.fog.color, '#c9a574', 0.85 * light + 0.1), near: base.fog.near * 0.3, far: base.fog.far * 0.4 };
    look.sky = { turbidity: 30, rayleigh: look.sky.rayleigh * 0.5, mie: 0.07, mieG: 0.55 };
  }
  return look;
}
